import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, TrendingUp, Save, RotateCcw, Info, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import api from '../../services/api';

/**
 * iter345 — Previous YTD wizard step.
 *
 * Captures (or auto-fills) the auditor-confirmed Previous-Year-To-Date
 * totals used by the Stock Audit Report. Backend handles year-end
 * windowing — this panel only surfaces what was returned, lets the
 * auditor edit, and POSTs back any changes. Auto-populates from the
 * last completed session's running YTD; if the year-end has lapsed
 * since then the row arrives zeroed and `is_first_audit_in_window`
 * is true.
 */
export default function PreviousYTDPanel({ sessionId, editable = true, onAdvance = null }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prev, setPrev] = useState(null);
  const [current, setCurrent] = useState(null);
  const [contrib, setContrib] = useState(null);
  const [form, setForm] = useState({
    actual_receipts_inc_vat: '',
    estimated_receipts_inc_vat: '',
    result_inc_vat: '',
    management_inc_vat: '',
    gross_profit: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/stocktake/sessions/${sessionId}/previous-ytd`);
      setPrev(res.data?.previous_ytd);
      setCurrent(res.data?.current_ytd);
      setContrib(res.data?.session_contribution);
      const p = res.data?.previous_ytd || {};
      const held = Array.isArray(p.held_fields) ? p.held_fields : [];
      setForm({
        actual_receipts_inc_vat: String(p.actual_receipts_inc_vat ?? '0'),
        estimated_receipts_inc_vat: String(p.estimated_receipts_inc_vat ?? '0'),
        result_inc_vat: String(p.result_inc_vat ?? '0'),
        // Show blank for held (unknown) fields so the auditor knows to enter a value
        management_inc_vat: held.includes('management_inc_vat') ? '' : String(p.management_inc_vat ?? '0'),
        gross_profit: String(p.gross_profit ?? '0'),
      });
    } catch (err) {
      toast({
        title: 'Could not load Previous YTD',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // iter426C (#6) — Estimated Receipts is now derived from
  // (Actual − Result), not user-input. GP% and Yield% are computed
  // on the client for instant feedback while typing, matching the
  // backend kpi_engine formulas verbatim. The auditor only inputs:
  // Actual Receipts (Inc VAT), Gross Profit, Result, Management.
  const derived = useMemo(() => {
    const actual = parseFloat(form.actual_receipts_inc_vat) || 0;
    const result = parseFloat(form.result_inc_vat) || 0;
    const mgmt = parseFloat(form.management_inc_vat) || 0;
    const gp = parseFloat(form.gross_profit) || 0;
    const estimated = actual - result;
    const denom = estimated + mgmt;
    const yieldPct = denom > 0 ? (actual / denom) * 100 : null;
    const gpPct = actual > 0 ? (gp / actual) * 100 : null;
    return { estimated, yieldPct, gpPct };
  }, [form]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, parseFloat(v) || 0])
      );
      // iter426C #6 — Derive estimated_receipts_inc_vat from
      // actual − result so the user never has to keep three figures
      // in sync. The backend stores the derived value verbatim.
      payload.estimated_receipts_inc_vat = (payload.actual_receipts_inc_vat || 0) - (payload.result_inc_vat || 0);
      payload.is_first_audit_in_window = !!prev?.is_first_audit_in_window;
      const res = await api.put(`/stocktake/sessions/${sessionId}/previous-ytd`, payload);
      setPrev(res.data?.previous_ytd);
      setCurrent(res.data?.current_ytd);
      toast({ title: 'Previous YTD saved' });
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResetToAuto = async () => {
    // Re-fetch with auto-populate path — overrides any user edits.
    setForm({
      actual_receipts_inc_vat: '0',
      estimated_receipts_inc_vat: '0',
      result_inc_vat: '0',
      management_inc_vat: '0',
      gross_profit: '0',
    });
    // The backend re-derives from prior-session YTD when user_edited
    // is false; saving zeroes flips user_edited true. To revert, we
    // delete the row by saving zeroes and then reload.
    await handleSave();
    await load();
  };

  const formatGBP = (v) => {
    if (v === null || v === undefined) return '—';
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
  };
  const formatPct = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}%`);

  // First-audit-of-window guard (per user spec, option 7b).
  const requiresEntry = useMemo(() => {
    if (!prev) return false;
    if (prev.user_edited) return false;
    if (!prev.is_first_audit_in_window) return false;
    // True iff this is the first audit AND the auditor hasn't saved.
    return true;
  }, [prev]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading Previous YTD…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="previous-ytd-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-amber-600" />Previous YTD
          {prev?.source === 'auto' && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded" data-testid="ytd-auto-filled-badge">
              auto-filled
            </span>
          )}
          {prev?.source === 'reset' && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded" data-testid="ytd-reset-badge">
              year-end reset
            </span>
          )}
          {prev?.source === 'imported' && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded" data-testid="ytd-imported-badge">
              Legacy historical import
            </span>
          )}
        </CardTitle>
        <CardDescription className="text-xs">
          Year-to-date totals running from <span className="font-mono">{prev?.ytd_window_start || '—'}</span>. Auto-filled from the prior audit's running YTD; review and override if needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {requiresEntry && (
          <div
            className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
            data-testid="ytd-first-audit-banner"
          >
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <div>
              <strong>First audit in this YTD window.</strong> Please review the values below before continuing — they default to zero and need to be confirmed.
            </div>
          </div>
        )}

        {/* PSB Stage 5 import banner */}
        {prev?.source_type === 'psb_s5' && !prev?.user_edited && (
          <div
            className="rounded border border-purple-300 bg-purple-50 px-3 py-2 text-xs text-purple-900 flex items-start gap-2"
            data-testid="ytd-psb-s5-banner"
          >
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-purple-600" />
            <div>
              <strong>Values loaded from historical archive.</strong> Actual receipts, result and gross profit were carried over from the archived period record.{' '}
              <span className="text-purple-700">Fields shown as blank are unknown in the legacy source — enter them before finalising.</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { key: 'actual_receipts_inc_vat', label: 'Actual Receipts (Inc VAT)' },
            { key: 'gross_profit', label: 'Gross Profit Value' },
            { key: 'result_inc_vat', label: 'Result Value' },
            { key: 'management_inc_vat', label: 'Management (Inc VAT)' },
          ].map(f => (
            <div key={f.key} className="space-y-1">
              <Label className="text-xs">{f.label}</Label>
              <Input
                type="number"
                step="0.01"
                value={form[f.key]}
                onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                disabled={!editable}
                className="h-9 text-sm"
                data-testid={`ytd-input-${f.key}`}
              />
            </div>
          ))}
        </div>

        {/* iter426C #6 — Derived figures box. Updates live as the
            auditor types in the inputs above. Mirrors the backend
            kpi_engine formulas verbatim so the saved YTD matches
            what the wizard previews. */}
        <div className="mt-2 rounded border border-slate-200 bg-slate-50/60 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-600 mb-1.5">
            Derived from your inputs
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
            <div data-testid="ytd-derived-estimated">
              <div className="text-muted-foreground">Estimated Receipts (Inc VAT)</div>
              <div className="font-semibold tabular-nums">
                {formatGBP(derived.estimated)}
                <span className="text-[10px] text-muted-foreground ml-1">= Actual − Result</span>
              </div>
            </div>
            <div data-testid="ytd-derived-gp-pct">
              <div className="text-muted-foreground">Gross Profit %</div>
              <div className="font-semibold tabular-nums">
                {formatPct(derived.gpPct)}
                <span className="text-[10px] text-muted-foreground ml-1">= GP / Actual × 100</span>
              </div>
            </div>
            <div data-testid="ytd-derived-yield-pct">
              <div className="text-muted-foreground">Yield %</div>
              <div className="font-semibold tabular-nums">
                {formatPct(derived.yieldPct)}
                <span className="text-[10px] text-muted-foreground ml-1">= Actual / (Est + Mgmt) × 100</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={!editable || saving} data-testid="ytd-save-btn">
            {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}Save
          </Button>
          {prev?.user_edited && (
            <Button variant="ghost" size="sm" onClick={handleResetToAuto} disabled={!editable || saving} data-testid="ytd-reset-btn">
              <RotateCcw className="h-3 w-3 mr-1" />Reset to auto-fill
            </Button>
          )}
          {onAdvance && (
            <div className="ml-auto">
              <Button size="sm" onClick={onAdvance} data-testid="ytd-advance-btn">Continue</Button>
            </div>
          )}
        </div>

        {/* Current YTD preview */}
        {current && (
          <div className="mt-3 border rounded p-3 bg-slate-50/40">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-600 mb-2 flex items-center gap-1">
              <Info className="h-3 w-3" />Current YTD (Previous + this audit)
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              <div data-testid="ytd-current-sales">
                <div className="text-muted-foreground">Sales (Inc VAT)</div>
                <div className="font-semibold">{formatGBP(current.sales_inc_vat)}</div>
              </div>
              <div data-testid="ytd-current-gp">
                <div className="text-muted-foreground">Gross Profit / GP %</div>
                <div className="font-semibold">{formatGBP(current.gross_profit)} <span className="text-muted-foreground font-normal">({formatPct(current.gp_pct)})</span></div>
              </div>
              <div data-testid="ytd-current-result">
                <div className="text-muted-foreground">Result / Result %</div>
                <div className="font-semibold">{formatGBP(current.result_inc_vat)} <span className="text-muted-foreground font-normal">({formatPct(current.result_pct)})</span></div>
              </div>
              <div data-testid="ytd-current-yield">
                <div className="text-muted-foreground">YTD Yield</div>
                <div className="font-semibold">{formatPct(current.ytd_yield_pct)}</div>
              </div>
              <div data-testid="ytd-current-management">
                <div className="text-muted-foreground">Management (Inc VAT)</div>
                <div className="font-semibold">{formatGBP(current.management_inc_vat)}</div>
              </div>
              <div data-testid="ytd-current-est-receipts">
                <div className="text-muted-foreground">Est Receipts (Inc VAT)</div>
                <div className="font-semibold">{formatGBP(current.estimated_receipts_inc_vat)}</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
